/**
 * Bloco de campos de Consultoria da proposta (PR-M1-01c-iii.a+.b,
 * refinado em PR-M1-01c-ii.d, cálculo automático em PR-M1-01c-ii.h).
 *
 * Usado quando `proposal_type` é `consulting` ou `saas_consulting`.
 * Estruturado em 2 subseções: investimento (parcelas, duração, modalidade)
 * e execução (horas semanais, valor hora extra).
 *
 * **Cálculo automático da parcela** (2026-04-24, pedido do Alexandre):
 *   Karla negocia o **investimento total** da consultoria, não a parcela.
 *   O sistema divide automaticamente pelo número de parcelas:
 *
 *     parcela = investimento_total / número_de_parcelas
 *
 *   - Input primário: `Investimento total (R$)` + `Número de parcelas`
 *   - Campo read-only: `Valor de cada parcela` (badge "calculado")
 *   - Trocar nº de parcelas **mantém o total fixo** e recomputa a parcela
 *     (a negociação é sobre valor cheio; parcelamento é decisão secundária)
 *   - Editar proposta existente: o total é seeded via `parcela × parcelas`
 *     (retro-compat com rascunhos antigos)
 *
 * **Por que não há mais campo "Forma de pagamento"** (2026-04-24):
 * toda cobrança roda via Asaas com `billingType: UNDEFINED` — o lead
 * escolhe PIX/boleto/cartão no momento de cada fatura. O campo virava
 * ruído informativo. Se um dia quisermos oferecer desconto por método,
 * a coluna `consulting_payment_method` continua no banco (nullable) e
 * basta reintroduzir o campo.
 *
 * Defaults calibrados pelo modelo atual da Karla Martins Consultoria
 * (ver docs/Proposta-Comercial-Dra-Danielle-Fabretti.md):
 *   - 6 parcelas × R$ 3.000 em 6 meses → investimento total R$ 18.000
 *   - 5h semanais, R$ 350/h extra
 *   - Modalidade híbrida
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Label, Select } from '@kmgestao/ui';

import {
  CONSULTING_MODALITIES,
  CONSULTING_MODALITY_LABELS,
  type CreateProposalInput,
} from '../../../features/proposals/index.js';

export interface ProposalConsultingFieldsProps {
  form: UseFormReturn<CreateProposalInput>;
  disabled: boolean;
}

const INPUT_CLASS =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm';

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

export function ProposalConsultingFields({ form, disabled }: ProposalConsultingFieldsProps) {
  const { register, setValue, watch } = form;

  // Campos persistidos no DB / payload — watched pra reagir no cálculo.
  // Double-cast via `unknown` porque o tipo de `watch()` é readonly[] |
  // primitive — TS strict bloqueia cast direto pra number.
  const count = watch('consulting_installments_count' as never) as unknown as number | undefined;
  const installment = watch('consulting_installment_brl' as never) as unknown as number | undefined;
  const weeklyHours = watch('consulting_weekly_hours' as never) as unknown as number | undefined;
  const durationMonths = watch('consulting_duration_months' as never) as unknown as
    | number
    | undefined;

  // **Total é o input primário** (local state — não persiste direto no DB).
  // Seed inicial = parcela × nº de parcelas, cobrindo:
  //   - Novo rascunho: defaults 6 × R$ 3.000 = R$ 18.000
  //   - Editar existente: valores vindos do banco
  const initialTotalRef = useRef<number | null>(null);
  if (initialTotalRef.current === null) {
    const c = typeof count === 'number' && count > 0 ? count : 0;
    const i = typeof installment === 'number' && installment > 0 ? installment : 0;
    initialTotalRef.current = Number((c * i).toFixed(2));
  }
  const [total, setTotal] = useState<number>(initialTotalRef.current);

  // Parcela derivada — exibida como read-only no UI.
  const computedInstallment = useMemo(() => {
    if (typeof count !== 'number' || count <= 0) return 0;
    return Number((total / count).toFixed(2));
  }, [total, count]);

  // **Taxa por hora contratada** — derivada de total / (horas × 4 × duração).
  // Usamos 4 semanas/mês (padrão intuitivo; 4.33 seria mais preciso mas
  // cria confusão pra quem quer fazer a conta de cabeça).
  // Alexandre 2026-04-24: "onde mostra o valor da hora contratada?"
  const computedHourlyRate = useMemo(() => {
    const h = typeof weeklyHours === 'number' ? weeklyHours : 0;
    const d = typeof durationMonths === 'number' ? durationMonths : 0;
    const totalHours = h * 4 * d;
    if (totalHours <= 0 || total <= 0) return 0;
    return Number((total / totalHours).toFixed(2));
  }, [total, weeklyHours, durationMonths]);

  // Sincroniza a parcela derivada de volta pro form (é o que vai pro DB).
  // Só atualiza se houve mudança meaningful (> 1 centavo) pra não criar
  // loops infinitos de render.
  useEffect(() => {
    if (!Number.isFinite(computedInstallment)) return;
    const current = typeof installment === 'number' ? installment : 0;
    if (Math.abs(computedInstallment - current) > 0.01) {
      setValue('consulting_installment_brl' as never, computedInstallment as never, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  }, [computedInstallment, installment, setValue]);

  const handleTotalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = Number.parseFloat(e.target.value);
    if (Number.isFinite(next) && next >= 0) {
      setTotal(next);
    } else if (e.target.value === '') {
      setTotal(0);
    }
  };

  return (
    <section className="space-y-4 rounded-md border border-border bg-muted/20 p-4">
      <header>
        <h4 className="text-sm font-semibold text-foreground">Consultoria</h4>
        <p className="text-xs text-muted-foreground">
          Karla negocia o <strong>investimento total</strong>; o valor da parcela é calculado
          automaticamente pelo número de parcelas.
        </p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="consulting_total_brl">Investimento total (R$)</Label>
          <input
            id="consulting_total_brl"
            type="number"
            min={0}
            step={0.01}
            inputMode="decimal"
            value={total || ''}
            onChange={handleTotalChange}
            disabled={disabled}
            className={INPUT_CLASS}
          />
          <p className="text-xs text-muted-foreground">
            Valor cheio negociado com o cliente. Default: R$ 18.000 (padrão 6 meses).
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="consulting_installments_count">Número de parcelas</Label>
          <input
            id="consulting_installments_count"
            type="number"
            min={1}
            max={60}
            inputMode="numeric"
            {...register('consulting_installments_count' as never, { valueAsNumber: true })}
            disabled={disabled}
            className={INPUT_CLASS}
          />
          <p className="text-xs text-muted-foreground">
            Trocar o nº de parcelas mantém o total e recalcula a parcela.
          </p>
        </div>
      </div>

      {/* Parcela computada — badge accent pra deixar claro que é derivada.
          Abaixo: taxa por hora contratada (apoia a decisão de preço da Karla). */}
      <div className="space-y-3 rounded-md border border-accent/30 bg-accent/5 p-4">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Valor de cada parcela
            </p>
            <p className="text-2xl font-bold text-foreground">
              {computedInstallment > 0 ? BRL.format(computedInstallment) : '—'}
              <span className="ml-2 text-sm font-normal text-muted-foreground">/ mês</span>
            </p>
          </div>
          <span className="inline-flex items-center rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-xs font-semibold text-accent">
            ✨ calculado automaticamente
          </span>
        </div>

        {/* Taxa hora derivada — só aparece quando há horas + duração válidas.
            Calculada com 4 semanas/mês (padrão simples). */}
        {computedHourlyRate > 0 ? (
          <div className="flex items-baseline gap-2 border-t border-accent/20 pt-3 text-sm">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Taxa hora contratada
            </span>
            <span className="text-base font-semibold text-foreground">
              ≈ {BRL.format(computedHourlyRate)} / h
            </span>
            <span className="text-xs text-muted-foreground">
              ({weeklyHours}h/sem × 4 × {durationMonths} meses ={' '}
              {(weeklyHours ?? 0) * 4 * (durationMonths ?? 0)}h totais)
            </span>
          </div>
        ) : null}

        {/* Input hidden pra manter o campo registrado no form (valueAsNumber)
            mesmo sendo visualmente read-only. O valor é setado via useEffect. */}
        <input
          type="hidden"
          {...register('consulting_installment_brl' as never, { valueAsNumber: true })}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="consulting_duration_months">Duração (meses)</Label>
          <input
            id="consulting_duration_months"
            type="number"
            min={1}
            max={60}
            inputMode="numeric"
            {...register('consulting_duration_months' as never, { valueAsNumber: true })}
            disabled={disabled}
            className={INPUT_CLASS}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="consulting_modality">Modalidade</Label>
          <Select
            id="consulting_modality"
            {...register('consulting_modality' as never)}
            disabled={disabled}
          >
            {CONSULTING_MODALITIES.map((m) => (
              <option key={m} value={m}>
                {CONSULTING_MODALITY_LABELS[m]}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="consulting_weekly_hours">Horas semanais</Label>
          <input
            id="consulting_weekly_hours"
            type="number"
            min={0}
            max={40}
            inputMode="numeric"
            {...register('consulting_weekly_hours' as never, { valueAsNumber: true })}
            disabled={disabled}
            className={INPUT_CLASS}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="consulting_extra_hour_brl">Valor hora extra (R$)</Label>
          <input
            id="consulting_extra_hour_brl"
            type="number"
            min={0}
            step={0.01}
            inputMode="decimal"
            {...register('consulting_extra_hour_brl' as never, { valueAsNumber: true })}
            disabled={disabled}
            className={INPUT_CLASS}
          />
          <p className="text-xs text-muted-foreground">
            Apenas para horas excedentes ao contrato (não entra no total).
          </p>
        </div>
      </div>

      <p className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Cobrança:</span> todas as parcelas da
        consultoria são processadas via Asaas. No momento de cada fatura, o cliente escolhe entre
        PIX, boleto ou cartão — o método é decidido pelo cliente, não pela proposta.
      </p>
    </section>
  );
}
